import React, { useCallback, useContext, useState } from 'react'
import ModalTest from '../../../BaseModal'
import * as S from '../../../styles/modal-style'
import axios from '../../../../config/axios'
import copywhite from '../../../../assets/production-flow/copywhite.svg'
import cross from '../../../../assets/production-flow/cross.svg'
import { DropdownHeaderSelectedItems, DropdownSelectedItemTag, StyledCard } from '../../../../pages/ClientPanel/Equipment/Machines/RegisterEquipment/style'
import { StyledFormGroup } from '../../../../pages/ClientPanel/Equipment/Vehicles/RegisterVehicleModal/style'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'

interface TransferModalProps {
  id: number
  onClickClose: () => void
}

interface OpItemType {
  id: number
  codeOfOP: string
}

const TransferModal = ({ id, onClickClose }: TransferModalProps) => {
  const [selectedOps, setSelectedOps] = useState<OpItemType[]>([])
  const [selectValue, setSelectValue] = useState<{ id: number; name: string; codeOfOP?: string } | null>(null)
  const { setFeedback } = useContext(FeedbackContext)

  const fetchOps = useCallback(async (limit: number, offset: number, search?: string) => {
    try {
      const page = Math.floor(offset / limit) + 1
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/services-for-transfer/${id}`,
        { params: { page, limit, ...(search?.trim() ? { search: search.trim() } : {}) } },
      )

      const apiData: { id: number; codeOfOP: string | null }[] = response.data?.data ?? []
      const selectedIds = new Set(selectedOps.map((op) => op.id))

      const mapped = apiData
        .filter((op) => !selectedIds.has(op.id))
        .map((op) => ({
          id: op.id,
          name: op.codeOfOP || `OP ${op.id}`,
          codeOfOP: op.codeOfOP ?? undefined,
        }))

      const result = mapped as typeof mapped & { _consumedCount?: number; _requestedCount?: number }
      result._consumedCount = apiData.length
      result._requestedCount = limit
      return result
    } catch (error) {
      console.error('Erro ao buscar OPs para transferência:', error)
      return []
    }
  }, [id, selectedOps])

  const handleTransferServices = async () => {
    if (selectedOps.length === 0) return

    try {
      await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/utils/copy-stages`,
        { serviceToCopyId: id, serviceToPasteId: selectedOps.map((op) => op.id) },
      )
      setFeedback({ isOpen: true, statusOk: true, message: 'Serviços transferidos com sucesso' })
      onClickClose()
    } catch (error) {
      setFeedback({ isOpen: true, statusOk: false, message: 'Erro ao transferir serviços' })
    }
  }

  const handleAddOp = (option: { id: number; name: string; codeOfOP?: string } | null) => {
    if (!option) {
      setSelectValue(null)
      return
    }

    if (selectedOps.some((op) => op.id === option.id)) {
      setSelectValue(null)
      return
    }

    setSelectedOps((prev) => [
      ...prev,
      { id: option.id, codeOfOP: option.codeOfOP || option.name },
    ])
    setSelectValue(null)
  }

  const handleRemoveOp = (opItemId: number) => {
    setSelectedOps((prev) => prev.filter((opItem) => opItem.id !== opItemId))
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='30rem'>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h3>Transferir serviços</h3>
          <button
            onClick={() => onClickClose()}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              background: '#f4f4f4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <img src={cross} alt="Fechar"/>
          </button>
        </div>
        <StyledCard
          style={{
            padding: '1rem',
            borderRadius: '12px',
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            boxShadow: 'none'
          }}
        >
          <StyledFormGroup style={{ gap: '0.5rem' }}>
            <InfiniteSelect
              label='Ordens de produção'
              placeholder='Selecione'
              searchPlaceholder='Buscar OP...'
              value={selectValue}
              onSelect={handleAddOp}
              fetchFunction={fetchOps}
              getOptionLabel={(opt) => opt.name || opt.codeOfOP || ''}
              validateSelection={(option) => {
                if (!option) return true
                return !selectedOps.some((op) => op.id === option.id)
              }}
              mode='readonly'
            />
            <DropdownHeaderSelectedItems
              style={{
                marginTop: '0.5rem',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}
            >
              {selectedOps.map((opItem) => (
                <DropdownSelectedItemTag key={opItem.id}>
                  {opItem.codeOfOP}
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveOp(opItem.id)
                    }}
                  >
                    x
                  </button>
                </DropdownSelectedItemTag>
              ))}
            </DropdownHeaderSelectedItems>
          </StyledFormGroup>
        </StyledCard>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          marginTop: '2rem'
        }}>
          <button
            onClick={() => onClickClose()}
            style={{
              padding: '0.625rem 1.5rem',
              border: '1px solid #0090D3',
              background: 'transparent',
              color: '#0090D3',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '700'
            }}
          >Cancelar</button>
          <button
            onClick={handleTransferServices}
            disabled={selectedOps.length === 0}
            style={{
              padding: '0.625rem 1.5rem',
              border: 'none',
              background: '#0090D3',
              color: 'white',
              borderRadius: '20px',
              cursor: selectedOps.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: selectedOps.length === 0 ? 0.6 : 1,
            }}
          >
            <img src={copywhite} alt="Colar serviços"/>
            Colar serviços
          </button>
        </div>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default TransferModal
